import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Target, DollarSign, Clock, BarChart3, Bot, Users, Layers, MessageCircle,
  Briefcase, TrendingUp, Sun, Activity, Sparkles, Rocket,
} from 'lucide-react';
import tokenStore from '../utils/tokenStore';
import { API_URL } from '../constants/api';
import { ROUTES } from '../constants/routes';
import { useToast } from '../context/ToastContext';
import { BUILD_TIME_OPTIONS } from '../constants/buildTimes';

/**
 * Six-step guided tour: a welcome overview, then one step per real tab.
 * Each "Next" click actually switches the live activeTab (via
 * props.setActiveTab) and re-renders the dashboard underneath a blurred
 * backdrop — so the tour teaches on the real app instead of a static
 * mockup of it. No setup/data-entry steps — this is orientation only.
 */
const STEPS = [
  {
    tab: ROUTES.TODAY, isWelcome: true, icon: Rocket, accent: '#2dd4bf',
    sub: 'Everything you need to build, track, and grow — in five sections. Here’s a quick look at each one.',
    items: [
      { icon: Sun, accent: '#6366f1', title: 'Today', desc: 'Daily HQ — momentum, schedule, what matters now.' },
      { icon: Activity, accent: '#f97316', title: 'Build', desc: 'Issues, milestones, priorities & focus sessions.' },
      { icon: Sparkles, accent: '#10b981', title: 'AI', desc: 'Your AI co-founder — strategy, decisions, unblocking.' },
      { icon: DollarSign, accent: '#0ea5e9', title: 'Money', desc: 'Financial health and runway.' },
      { icon: Users, accent: '#3b82f6', title: 'Team', desc: 'Workload, objectives & hiring plan.' },
    ],
  },
  {
    tab: ROUTES.TODAY, title: 'Today', icon: Sun, accent: '#6366f1',
    sub: 'Your daily command center — open it every morning.',
    items: [
      { icon: BarChart3, title: 'Momentum Score', desc: 'Daily progress tracked automatically from your activity.' },
      { icon: Target, title: 'Milestones', desc: 'Your top priorities and deadlines at a glance.' },
      { icon: Clock, title: "Today's Schedule", desc: "Time blocks, focus sessions, and what's next." },
      { icon: DollarSign, title: 'Runway', desc: 'Cash on hand and burn rate — always visible.' },
    ],
  },
  {
    tab: ROUTES.BUILD, title: 'Build', icon: Activity, accent: '#f97316',
    sub: 'Where the work happens.',
    items: [
      { icon: Layers, title: 'Board', desc: 'Issues, tasks & priorities, all in one place.' },
      { icon: Target, title: 'Milestones', desc: 'Goals, deadlines & progress.' },
    ],
  },
  {
    tab: ROUTES.AI, title: 'AI Co-founder', icon: Sparkles, accent: '#10b981',
    sub: "Think of it as a co-founder who's always available.",
    items: [
      { icon: Bot, title: 'Strategy & Decisions', desc: 'Talk through pricing, positioning, hiring, pivots — anything.' },
      { icon: MessageCircle, title: 'Context-Aware', desc: 'It knows your milestones, runway, and build progress.' },
      { icon: Target, title: 'Unblock Yourself', desc: 'Stuck on something? Get a second opinion instantly.' },
    ],
  },
  {
    tab: ROUTES.COMPANY, title: 'Money', icon: DollarSign, accent: '#0ea5e9',
    sub: 'Stay on top of your financial health.',
    items: [
      { icon: DollarSign, title: 'Cash on Hand', desc: 'Track your current balance and how long it lasts.' },
      { icon: TrendingUp, title: 'Burn Rate & Runway', desc: 'Know your monthly burn and months of runway remaining.' },
      { icon: BarChart3, title: 'Financial Health', desc: 'Visualize income, expenses, and key financial milestones.' },
    ],
  },
  {
    tab: ROUTES.TEAM, title: 'Team', icon: Users, accent: '#3b82f6',
    sub: 'Keep your team aligned.',
    items: [
      { icon: Users, title: 'Team Members', desc: 'Add and manage your current team, roles, and responsibilities.' },
      { icon: BarChart3, title: 'Workload', desc: 'See who is working on what and balance the load.' },
      { icon: Target, title: 'Objectives', desc: 'Set team-wide goals and track progress together.' },
      { icon: Briefcase, title: 'Hiring Plan', desc: 'Map out the roles you need to fill as you grow.' },
    ],
    isLast: true,
  },
];

// The five tab steps, numbered independently of the Welcome page (which
// isn't counted — "Step 1 of 5" starts at Today, not at Welcome).
const TAB_STEPS = STEPS.filter(s => !s.isWelcome);

const OnboardingWizard = ({ currentUser, activeTab, setActiveTab, demoTourOpen, onComplete, onEmailPrefs }) => {
  const { showToast } = useToast();
  // "When do you usually work on your startup?" — one tap, saved immediately,
  // optional. Sets the brief to arrive about an hour before they start.
  const [buildTime, setBuildTime] = useState(currentUser?.email_preferences?.build_time || '');
  const [buildTimeSaving, setBuildTimeSaving] = useState(false);
  const [index, setIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const step = STEPS[index];
  const firstName = currentUser?.full_name?.split(' ')?.[0] || currentUser?.username || 'Founder';
  const stepTitle = step.isWelcome ? `${firstName}, welcome to Founders+` : step.title;

  useEffect(() => {
    if (activeTab !== step.tab) setActiveTab(step.tab);
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Step out of the way while the sample-data demo plays — otherwise its
  // overlay renders underneath this one's blur and z-index and is invisible.
  if (demoTourOpen) return null;

  const finishOnboarding = async () => {
    setFinishing(true);
    try {
      const token = tokenStore.get();
      await axios.post(`${API_URL}/api/beta/complete-onboarding`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch (err) {
      console.error('Failed to mark onboarding complete:', err.message);
    }
    setFinishing(false);
    onComplete();
  };

  const chooseBuildTime = async (value) => {
    const previous = buildTime;
    setBuildTime(value);
    setBuildTimeSaving(true);
    try {
      const token = tokenStore.get();
      const res = await axios.put(`${API_URL}/api/users/me/build-time`, { build_time: value }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (onEmailPrefs && res.data?.email_preferences) onEmailPrefs(res.data.email_preferences);
    } catch (err) {
      setBuildTime(previous);
      showToast("Couldn't save that — you can set it later in Settings.", 'error');
    }
    setBuildTimeSaving(false);
  };

  const handleNext = () => {
    if (step.isLast) { finishOnboarding(); return; }
    setFading(true);
    setTimeout(() => {
      const next = STEPS[index + 1];
      setActiveTab(next.tab);
      setIndex(i => i + 1);
      setFading(false);
    }, 180);
  };

  const handleBack = () => {
    if (index === 0) return;
    setFading(true);
    setTimeout(() => {
      const prev = STEPS[index - 1];
      setActiveTab(prev.tab);
      setIndex(i => i - 1);
      setFading(false);
    }, 180);
  };

  const handleSkip = () => {
    showToast('Onboarding skipped — explore any tab anytime.', 'info');
    finishOnboarding();
  };

  const openDemoTour = () => {
    if (typeof window !== 'undefined' && typeof window.__openDemoTour === 'function') {
      window.__openDemoTour();
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(4, 9, 16, 0.55)', backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
      padding: 16, opacity: visible ? 1 : 0, transition: 'opacity 0.25s ease',
    }}>
      <div style={{
        width: '100%', maxWidth: 400,
        backgroundColor: 'var(--card-bg-solid, #111820)',
        border: '1px solid var(--card-border)',
        borderRadius: 16, padding: '22px 22px 18px',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.4)',
        opacity: fading || finishing ? 0.4 : 1,
        transform: fading ? 'translateY(6px)' : 'translateY(0)',
        transition: 'opacity 0.18s ease, transform 0.18s ease',
      }}>
        {/* Progress dots — only for the 5 tab steps; Welcome isn't counted */}
        {!step.isWelcome && (
          <div style={{ display: 'flex', gap: 5, marginBottom: 16 }}>
            {TAB_STEPS.map((s, i) => (
              <div key={i} style={{
                width: 18, height: 4, borderRadius: 2,
                backgroundColor: i < index ? step.accent : 'var(--border-subtle)',
                transition: 'background-color 0.2s',
              }} />
            ))}
          </div>
        )}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 9,
            backgroundColor: step.accent + '20',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {React.createElement(step.icon || Target, { size: 16, style: { color: step.accent } })}
          </div>
          <div>
            {!step.isWelcome && (
              <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: step.accent }}>
                Step {index} of {TAB_STEPS.length}
              </div>
            )}
            <h2 style={{ margin: step.isWelcome ? 0 : '1px 0 0', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
              {stepTitle}
            </h2>
          </div>
        </div>

        <p style={{ margin: '8px 0 14px', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {step.sub}
        </p>

        {/* Feature list for this tab */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16 }}>
          {step.items.map((item, i) => {
            const itemAccent = item.accent || step.accent;
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '9px 10px', borderRadius: 8,
                backgroundColor: 'var(--interactive-default)',
                border: '1px solid var(--border-subtle)',
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: 6, marginTop: 1, flexShrink: 0,
                  backgroundColor: itemAccent + '20',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {React.createElement(item.icon, { size: 12, style: { color: itemAccent } })}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{item.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, lineHeight: 1.4 }}>{item.desc}</div>
                </div>
              </div>
            );
          })}
        </div>

        {step.isWelcome && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 7 }}>
              When do you usually work on your startup?
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }}>
              {BUILD_TIME_OPTIONS.map(o => {
                const selected = buildTime === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    disabled={buildTimeSaving}
                    aria-pressed={selected}
                    onClick={() => chooseBuildTime(o.value)}
                    style={{
                      padding: '8px 10px', borderRadius: 8, textAlign: 'left',
                      cursor: buildTimeSaving ? 'default' : 'pointer',
                      backgroundColor: selected ? step.accent + '22' : 'var(--interactive-default)',
                      border: `1px solid ${selected ? step.accent : 'var(--border-subtle)'}`,
                      color: 'var(--text-primary)',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{o.label}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 1 }}>{o.hint}</div>
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 6 }}>
              {buildTime
                ? 'Saved — your daily brief will arrive about an hour before you start. Change it any time in Settings.'
                : 'Optional — your daily brief arrives about an hour before you usually start.'}
            </div>
          </div>
        )}

        {step.isWelcome && (
          <button
            type="button"
            onClick={openDemoTour}
            title="Watch a 22-second guided tour with sample data — nothing is saved to your workspace."
            style={{
              width: '100%', padding: '9px 14px', marginBottom: 16,
              backgroundColor: 'transparent', color: 'var(--text-secondary)',
              border: '1px solid var(--border-subtle)', borderRadius: 8,
              fontSize: 12, fontWeight: 500, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <span style={{ fontSize: 11 }}>▶</span>
            <span>Or watch the 22-second tour with sample data</span>
          </button>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <button
            onClick={handleSkip}
            disabled={finishing}
            style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: 12, fontWeight: 600, cursor: finishing ? 'default' : 'pointer', padding: '6px 4px',
            }}
          >
            Skip onboarding
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {index > 0 && (
              <button
                onClick={handleBack}
                disabled={finishing || fading}
                style={{
                  padding: '9px 14px', borderRadius: 8,
                  backgroundColor: 'var(--interactive-default)', color: 'var(--text-secondary)',
                  border: '1px solid var(--card-border)',
                  fontSize: 12.5, fontWeight: 600, cursor: finishing || fading ? 'default' : 'pointer',
                }}
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={finishing || fading}
              style={{
                padding: '9px 18px', borderRadius: 8, border: 'none',
                backgroundColor: step.accent, color: '#fff',
                fontSize: 12.5, fontWeight: 700, cursor: finishing || fading ? 'default' : 'pointer',
                opacity: finishing ? 0.7 : 1, transition: 'opacity 0.15s',
              }}
            >
              {finishing ? 'Finishing...' : step.isLast ? 'Finish' : `Next: ${STEPS[index + 1].title}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
